import { useEffect, useRef, useState } from 'react';
import { MOCK_INCIDENTS, MOCK_SAFE_ZONES } from 'utils/mockData';
import { formatTimeAgo } from 'utils/helpers';

const SEVERITY_COLORS = {
  critical: '#FF3B3B',
  high: '#FFB300',
  medium: '#1E90FF',
  low: '#00C896',
};

const TYPE_ICONS = {
  fire: '🔥',
  medical: '🚑',
  flood: '🌊',
  other: '⚠️',
};

export default function IncidentMap({
  incidents = MOCK_INCIDENTS,
  showHeatmap = false,
  showSafeZones = true,
  centerLat = 20.2961,
  centerLng = 85.8245,
  zoom = 13,
  height = '400px',
  userLocation = null,
  responderLocation = null,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [heatmapVisible, setHeatmapVisible] = useState(showHeatmap);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (mapInstanceRef.current) return;

    const init = async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');

      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        center: [centerLat, centerLng],
        zoom,
        zoomControl: true,
        attributionControl: false,
      });

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        { maxZoom: 19 }
      ).addTo(map);

      mapInstanceRef.current = map;

      // Plot incidents
      incidents.forEach((inc) => {
        const color = SEVERITY_COLORS[inc.severity] || '#1E90FF';
        const icon = L.divIcon({
          className: '',
          html: `
            <div style="position:relative;width:36px;height:36px;">
              <div style="
                width:36px;height:36px;border-radius:50%;
                background:${color}22;border:2px solid ${color};
                display:flex;align-items:center;justify-content:center;
                font-size:16px;cursor:pointer;
                box-shadow:0 0 12px ${color}80;
              ">${TYPE_ICONS[inc.type] || '⚠️'}</div>
              ${inc.status === 'active' ? `<div style="
                position:absolute;top:-4px;right:-4px;
                width:10px;height:10px;border-radius:50%;
                background:${color};border:2px solid #0A0F1E;
                animation:pulse 1.5s infinite;
              "></div>` : ''}
            </div>`,
          iconSize: [36, 36],
          iconAnchor: [18, 18],
        });

        const marker = L.marker([inc.location.lat, inc.location.lng], { icon });
        marker.addTo(map);
        marker.bindPopup(`
          <div style="font-family:Rajdhani,sans-serif;min-width:180px;">
            <div style="font-weight:700;font-size:14px;color:${color};margin-bottom:6px;">
              ${TYPE_ICONS[inc.type]} ${inc.type.toUpperCase()} — ${inc.severity.toUpperCase()}
            </div>
            <div style="font-size:12px;color:#aaa;margin-bottom:4px;">📍 ${inc.location.address}</div>
            <div style="font-size:12px;color:#aaa;margin-bottom:4px;">⏱ ${formatTimeAgo(inc.createdAt)}</div>
            <div style="font-size:12px;color:#aaa;margin-bottom:8px;">👥 ${inc.peopleAffected} affected</div>
            <div style="
              display:inline-block;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:700;
              background:${color}22;color:${color};border:1px solid ${color}44;
            ">${inc.status.toUpperCase()}</div>
          </div>
        `);
        markersRef.current.push(marker);
      });

      // Safe zones
      if (showSafeZones) {
        MOCK_SAFE_ZONES.forEach((sz) => {
          const szIcon = L.divIcon({
            className: '',
            html: `<div style="
              width:28px;height:28px;border-radius:50%;
              background:#00C89622;border:2px solid #00C896;
              display:flex;align-items:center;justify-content:center;
              font-size:14px;box-shadow:0 0 8px #00C89660;
            ">🛡️</div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
          });
          const m = L.marker([sz.lat, sz.lng], { icon: szIcon });
          m.addTo(map);
          m.bindPopup(`
            <div style="font-family:Rajdhani,sans-serif;">
              <div style="font-weight:700;color:#00C896;margin-bottom:4px;">🛡️ ${sz.name}</div>
              <div style="font-size:12px;color:#aaa;">Capacity: ${sz.occupancy}/${sz.capacity}</div>
              <div style="font-size:12px;color:#aaa;">Distance: ${sz.distance}</div>
            </div>
          `);
        });
      }

      // User location
      if (userLocation) {
        const uIcon = L.divIcon({
          className: '',
          html: `<div style="
            width:16px;height:16px;border-radius:50%;
            background:#1E90FF;border:3px solid white;
            box-shadow:0 0 12px #1E90FF;
          "></div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
        L.marker([userLocation.lat, userLocation.lng], { icon: uIcon })
          .addTo(map)
          .bindPopup('<div style="color:#1E90FF;font-weight:700;">📍 Your Location</div>');
      }

      // Responder location
      if (responderLocation) {
        const rIcon = L.divIcon({
          className: '',
          html: `<div style="
            width:18px;height:18px;border-radius:50%;
            background:#FFB300;border:3px solid white;
            box-shadow:0 0 12px #FFB300;
          "></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        });
        L.marker([responderLocation.lat, responderLocation.lng], { icon: rIcon })
          .addTo(map)
          .bindPopup('<div style="color:#FFB300;font-weight:700;">🚒 Responder Location</div>');
      }

      setIsLoaded(true);
    };

    init();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markersRef.current = [];
      }
    };
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden border border-navy-border" style={{ height }}>
      {/* Map controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
        <button
          onClick={() => setHeatmapVisible(!heatmapVisible)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
            heatmapVisible
              ? 'bg-electric border-electric text-navy'
              : 'bg-navy-card border-navy-border text-gray-300 hover:border-electric'
          }`}
        >
          🌡️ Heatmap
        </button>
        <button
          onClick={() => {
            if (mapInstanceRef.current) {
              mapInstanceRef.current.setView([centerLat, centerLng], zoom);
            }
          }}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-navy-card border border-navy-border text-gray-300 hover:border-electric transition-all"
        >
          🎯 Reset
        </button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-10 glass-card p-2.5 text-xs space-y-1.5">
        {[
          { color: '#FF3B3B', label: 'Critical' },
          { color: '#FFB300', label: 'High' },
          { color: '#1E90FF', label: 'Medium' },
          { color: '#00C896', label: 'Safe Zone' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
            <span className="text-gray-300">{l.label}</span>
          </div>
        ))}
      </div>

      {/* Loading overlay */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-navy flex items-center justify-center z-20">
          <div className="flex flex-col items-center gap-3">
            <div className="spinner w-8 h-8" />
            <span className="text-gray-400 text-sm font-mono">Loading tactical map...</span>
          </div>
        </div>
      )}

      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}
